import React, { useCallback, useEffect } from 'react';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { ToastItem } from './ToastItem';
import { toastManager } from './toastManager';
import type { ToastItemProps, ToastPosition } from './types';

type AnimatedToastItemProps = ToastItemProps & {
  position: ToastPosition;
};

const OFFSCREEN_DISTANCE = 100;
const SPRING_CONFIG = { damping: 20, stiffness: 300 };
const SWIPE_OUT_CONFIG = { duration: 200 };

const SLIDE_DURATION_MS = 400;
const SLIDE_IN_CONFIG = {
  duration: SLIDE_DURATION_MS,
  easing: Easing.out(Easing.cubic),
};
const SLIDE_OUT_CONFIG = {
  duration: SLIDE_DURATION_MS,
  easing: Easing.in(Easing.cubic),
};

const AnimatedToastItem = ({
  toast,
  onDismiss,
  position,
}: AnimatedToastItemProps) => {
  // Sign of the direction the toast travels to leave the screen.
  const away = position === 'top' ? -1 : 1;

  const translateY = useSharedValue(away * OFFSCREEN_DISTANCE);
  const opacity = useSharedValue(0);

  useEffect(() => {
    translateY.value = withTiming(0, SLIDE_IN_CONFIG);
    opacity.value = withTiming(1, SLIDE_IN_CONFIG);
  }, [translateY, opacity, away]);

  const slideOut = useCallback(() => {
    'worklet';

    translateY.value = withTiming(away * OFFSCREEN_DISTANCE, SLIDE_OUT_CONFIG);
    opacity.value = withTiming(0, SLIDE_OUT_CONFIG, (finished) => {
      if (finished) runOnJS(onDismiss)(toast.id);
    });
  }, [translateY, opacity, away, onDismiss, toast.id]);

  const swipeOut = () => {
    'worklet';

    translateY.value = withTiming(away * OFFSCREEN_DISTANCE, SWIPE_OUT_CONFIG);
    opacity.value = withTiming(0, SWIPE_OUT_CONFIG, () => {
      runOnJS(onDismiss)(toast.id);
    });
  };

  // toastManager removes the toast at `duration`; start the slide-out early
  // so it has finished by then instead of the toast vanishing mid-screen.
  useEffect(() => {
    const lifetime = toast.duration ?? toastManager.getDefaultDuration();

    if (lifetime <= 0) return;

    const timeout = setTimeout(
      slideOut,
      Math.max(lifetime - SLIDE_DURATION_MS, 0),
    );

    return () => clearTimeout(timeout);
  }, [slideOut, toast.duration]);

  const panGesture = Gesture.Pan()
    .onUpdate((event) => {
      if (position === 'top') {
        if (event.translationY < 0) {
          translateY.value = event.translationY;
          opacity.value = 1 + event.translationY / 100;
        }
      } else {
        if (event.translationY > 0) {
          translateY.value = event.translationY;
          opacity.value = 1 - event.translationY / 100;
        }
      }
    })
    .onEnd((event) => {
      const threshold = 50;
      const shouldDismiss =
        position === 'top'
          ? event.translationY < -threshold || event.velocityY < -threshold
          : event.translationY > threshold || event.velocityY > threshold;

      if (shouldDismiss) {
        swipeOut();
      } else {
        translateY.value = withSpring(0, SPRING_CONFIG);
        opacity.value = withTiming(1, SWIPE_OUT_CONFIG);
      }
    })
    .withTestId('toast-pan-gesture');

  const tapGesture = Gesture.Tap()
    .onEnd((_event, success) => {
      if (success) slideOut();
    })
    .withTestId('toast-tap-gesture');

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: translateY.value }],
      opacity: opacity.value,
    };
  });

  return (
    <GestureDetector gesture={Gesture.Race(panGesture, tapGesture)}>
      <Animated.View style={animatedStyle} testID="animated-toast-container">
        <ToastItem toast={toast} onDismiss={onDismiss} />
      </Animated.View>
    </GestureDetector>
  );
};

AnimatedToastItem.displayName = 'AnimatedToastItem';

export { AnimatedToastItem };
